import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

export function ProgressBar({
    value,
    total,
    className,
}: {
    value: number
    total: number
    className?: string
}) {
    const ratio = total > 0 ? Math.max(0, Math.min(1, value / total)) : 0

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={value}
            className={cn('relative h-1.5 overflow-hidden rounded-full bg-wb-panel-hi', className)}
        >
            <div
                data-testid="progress-fill"
                className="h-full rounded-full bg-wb-accent transition-[width] duration-300"
                style={{ width: `${ratio * 100}%` }}
            />
        </div>
    )
}

/** Zählt auf den Zielwert hoch - der Moment, in dem die Bilanz erscheint. */
export function AnimatedCounter({
    value,
    duration = 1200,
    decimals = 2,
    suffix = ' €',
    signed = false,
}: {
    value: number
    duration?: number
    decimals?: number
    suffix?: string
    signed?: boolean
}) {
    const reduceMotion = useMemo(
        () =>
            typeof window !== 'undefined' &&
            (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false),
        [],
    )
    const [current, setCurrent] = useState(reduceMotion ? value : 0)

    useEffect(() => {
        if (reduceMotion) {
            return
        }

        const start = performance.now()
        let frame = 0

        const tick = (now: number) => {
            const progress = Math.min(1, (now - start) / duration)

            setCurrent(value * (1 - Math.pow(1 - progress, 3)))

            if (progress < 1) {
                frame = requestAnimationFrame(tick)
            }
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [value, duration, reduceMotion])

    const sign = signed ? (current >= 0 ? '+' : '−') : ''
    const shown = signed ? Math.abs(current) : current

    return (
        <span className="wb-num">
            {sign}
            {shown.toFixed(decimals).replace('.', ',')}
            {suffix}
        </span>
    )
}

export function Headline({
    children,
    className,
    as: Tag = 'h1',
}: {
    children: React.ReactNode
    className?: string
    as?: 'h1' | 'h2'
}) {
    return <Tag className={cn('wb-display text-pretty text-wb-text', className)}>{children}</Tag>
}

const TONES = {
    accent: 'text-wb-accent',
    win: 'text-wb-win',
    loss: 'text-wb-loss',
    text: 'text-wb-text',
    dim: 'text-wb-dim',
} as const

export function StatCard({
    label,
    value,
    tone = 'text',
    className,
}: {
    label: string
    value: React.ReactNode
    tone?: keyof typeof TONES
    className?: string
}) {
    return (
        <div
            className={cn(
                'flex min-w-0 flex-1 flex-col gap-1 rounded-[14px] border border-wb-border bg-wb-panel px-3 py-2.5',
                className,
            )}
        >
            <span className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                {label}
            </span>
            <span className={cn('wb-num text-lg leading-none font-extrabold', TONES[tone])}>
                {value}
            </span>
        </div>
    )
}

/**
 * Zwei Balken im Vergleich - „das dachtest du, so war es wirklich".
 */
export function CompareBar({
    label,
    value,
    max,
    tone,
}: {
    label: string
    value: number
    max: number
    tone: 'warn' | 'loss' | 'win'
}) {
    const width = max > 0 ? Math.min(100, (Math.abs(value) / max) * 100) : 0

    return (
        <div>
            <div className="flex items-baseline justify-between">
                <span className="text-[11px] font-bold tracking-[.06em] text-wb-dim uppercase">
                    {label}
                </span>
                <span
                    className={cn(
                        'wb-num text-base font-extrabold',
                        tone === 'warn' && 'text-wb-warn',
                        tone === 'loss' && 'text-wb-loss',
                        tone === 'win' && 'text-wb-win',
                    )}
                >
                    {value >= 0 ? '+' : '−'}
                    {Math.abs(value).toFixed(2).replace('.', ',')} €
                </span>
            </div>
            <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-wb-panel-hi">
                <div
                    data-testid={`compare-${tone}`}
                    className={cn(
                        'h-full rounded-full',
                        tone === 'warn' && 'bg-wb-warn',
                        tone === 'loss' && 'bg-wb-loss',
                        tone === 'win' && 'bg-wb-win',
                    )}
                    style={{ width: `${width}%` }}
                />
            </div>
        </div>
    )
}
